import type { SpendingCategorySummary } from '../../lib/api';

const CATEGORY_STYLES: Record<string, { bg: string; bar: string }> = {
  Builder: { bg: 'bg-blue-500/10', bar: 'bg-blue-500' },
  Publishing: { bg: 'bg-purple-500/10', bar: 'bg-purple-500' },
  'AI Usage': { bg: 'bg-emerald-500/10', bar: 'bg-emerald-500' },
  Storage: { bg: 'bg-amber-500/10', bar: 'bg-amber-500' },
  'File Operations': { bg: 'bg-cyan-500/10', bar: 'bg-cyan-500' },
  Other: { bg: 'bg-gray-500/10', bar: 'bg-gray-400' },
};

interface SpendingSummaryProps {
  categories: SpendingCategorySummary[];
  loading: boolean;
}

export function SpendingSummary({ categories, loading }: SpendingSummaryProps) {
  if (loading) {
    return (
      <div className="bg-card rounded-lg shadow-sm border border-border p-6">
        <div className="animate-pulse">
          <div className="h-5 bg-muted rounded w-40 mb-4"></div>
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-10 bg-muted rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (categories.length === 0) {
    return (
      <div className="bg-card rounded-lg shadow-sm border border-border p-6">
        <h2 className="text-sm font-medium text-muted-foreground mb-3">Spending Breakdown</h2>
        <p className="text-sm text-muted-foreground">No spending yet</p>
      </div>
    );
  }

  const maxTotal = Math.max(...categories.map((c) => c.totalCents));
  const grandTotal = categories.reduce((sum, c) => sum + c.totalCents, 0);

  return (
    <div className="bg-card rounded-lg shadow-sm border border-border p-6">
      <div className="flex items-baseline justify-between mb-4">
        <h2 className="text-sm font-medium text-muted-foreground">Spending Breakdown</h2>
        <span className="text-sm text-muted-foreground">
          ${(grandTotal / 100).toFixed(2)} total
        </span>
      </div>
      <div className="space-y-3">
        {categories.map((cat) => {
          const styles = CATEGORY_STYLES[cat.category] ?? CATEGORY_STYLES.Other;
          const pct = maxTotal > 0 ? (cat.totalCents / maxTotal) * 100 : 0;
          return (
            <div key={cat.category}>
              <div className="flex items-baseline justify-between mb-1">
                <span className="text-sm font-medium text-foreground">{cat.category}</span>
                <div className="flex items-baseline gap-2">
                  <span className="text-xs text-muted-foreground">{cat.count} txns</span>
                  <span className="text-sm font-medium text-foreground">
                    ${(cat.totalCents / 100).toFixed(2)}
                  </span>
                </div>
              </div>
              <div className={`h-2 rounded-full ${styles.bg}`}>
                <div
                  className={`h-2 rounded-full ${styles.bar} transition-all`}
                  style={{ width: `${Math.max(pct, 1)}%` }}
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
